import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, distinctUntilChanged } from 'rxjs';
import {
  IExpandAndFullScreenChange,
  SessionSharedDataService,
  SessionView,
} from 'src/app/services/session-shared-data.service';
import { isObserverTabId } from 'src/app/services/observer-mode.utils';
import { UserService, SPECTATOR_SUFFIX } from 'src/app/services/user.service';
import {
  TemporaryUserMetadataEntry,
  TemporaryUserMetadataRepositoryService,
} from '../../temporary-user-metadata-repository.service';
import { SpaceRepository } from '../../space.repository';
import { Location } from '../../temporary-user-metadata-repository.service';

export interface ISpaceFollowMode {
  followingID?: string;
  followingTabID?: string;
  followingFrameID?: string;
  followingInitLocation?: Location;
  followingSpaceView?: SessionView;
  followingSessionViewMetaData?: any;
  followingExpandAndFullScreenChange?: IExpandAndFullScreenChange;
  followers?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class FollowModeRepositoryService {
  private _spaceFollowMode$: BehaviorSubject<ISpaceFollowMode> =
    new BehaviorSubject<ISpaceFollowMode>(this.defaultState);
  public spaceFollowMode$ = this._spaceFollowMode$.asObservable();
  constructor(
    private temporaryUserMetadatapositoryService: TemporaryUserMetadataRepositoryService,
    private spaceRepository: SpaceRepository,
    private userService: UserService,
    private sharedDataService: SessionSharedDataService,
  ) {
    combineLatest([
      this.temporaryUserMetadatapositoryService.spaceTemporaryMetadata$,
      this.spaceRepository.activeSpaceCurrentRoomUid$.pipe(distinctUntilChanged(), filter(Boolean)),
    ]).subscribe(([metadata, currentRoomUid]) => {
      this.updateFollowModeStates(metadata, currentRoomUid);
    });
  }

  public getCurrentFollowModeState(): ISpaceFollowMode {
    return this._spaceFollowMode$.getValue();
  }

  public resetFollowModeState() {
    this._spaceFollowMode$.next(this.defaultState);
  }

  private updateFollowModeStates(
    metadata: Map<string, TemporaryUserMetadataEntry>,
    currentRoomUid: string,
  ) {
    const currentTabHash = this.userService.userUniqueHash;
    const currentTabEntry = metadata.get(currentTabHash);

    const newFollowModeState: ISpaceFollowMode = this.defaultState;
    const newFollowers: Set<string> = new Set();

    // Single iteration to find both followers AND spectators - O(n)
    for (const [hash, tabEntry] of metadata) {
      // Check for followers in current room
      if (tabEntry.breakoutRoomUid !== currentRoomUid) {
        continue;
      }
      if (tabEntry.followingTabID === currentTabHash) {
        if (hash.includes(SPECTATOR_SUFFIX) || isObserverTabId(hash)) {
          newFollowers.add(hash);
          continue;
        }
        newFollowers.add(tabEntry.userId);
      }
    }

    newFollowModeState.followers = [...newFollowers];

    // handle following
    if (currentTabEntry?.followingTabID && currentTabEntry?.followingID) {
      // when the current user is part of a chain / cycle locally break it so curr user
      // stops sending updates to the followers.
      // on the followers side they will get updates from the leaf node.
      newFollowModeState.followers = [];

      newFollowModeState.followingTabID = currentTabEntry.followingTabID;
      newFollowModeState.followingID = currentTabEntry.followingID;

      if (metadata.has(currentTabEntry.followingTabID)) {
        const followingTabEntry = this.getLeafFollowingNode(
          metadata,
          currentTabEntry.followingTabID,
        );

        // undefined means cycle detected keep the user state as it is
        if (!followingTabEntry) {
          newFollowModeState.followingSpaceView =
            this.sharedDataService.sessionView.getSessionView();
          newFollowModeState.followingSessionViewMetaData =
            this.sharedDataService.sessionView.getSessionViewMetaData();
          newFollowModeState.followingExpandAndFullScreenChange =
            this.sharedDataService.sessionView.expandedFullScreenChange() ?? undefined;
          newFollowModeState.followingFrameID = this.spaceRepository.activeSpaceSelectedBoardUid;
        }

        // if the following user meta data still states he is not being followed.
        // this means it's stale / out dated data , so we continue in the pending state
        // till we get the fresh data updates
        else if (followingTabEntry?.isBeingFollowed) {
          newFollowModeState.followingFrameID = followingTabEntry?.frameUID;
          newFollowModeState.followingInitLocation = followingTabEntry?.location;
          newFollowModeState.followingSpaceView = followingTabEntry?.sessionView;
          newFollowModeState.followingSessionViewMetaData = followingTabEntry?.sessionViewMetaData;
          newFollowModeState.followingExpandAndFullScreenChange =
            followingTabEntry?.expandAndFullScreenChange;
        }
      }
    }

    this._spaceFollowMode$.next(newFollowModeState);
  }

  private getLeafFollowingNode(
    metadata: Map<string, TemporaryUserMetadataEntry>,
    followingTabID: string,
  ): TemporaryUserMetadataEntry | undefined {
    const usersInTheChain: Map<string, TemporaryUserMetadataEntry> = new Map();
    let followingTabEntry = metadata.get(followingTabID);

    while (followingTabEntry?.followingTabID && followingTabEntry.userUniqueHash) {
      // cycle detected return undefined in that case
      if (usersInTheChain.has(followingTabEntry.userUniqueHash)) {
        return undefined;
      }

      usersInTheChain.set(followingTabEntry.userUniqueHash, followingTabEntry);
      followingTabEntry = metadata.get(followingTabEntry.followingTabID);
    }

    return followingTabEntry;
  }

  private get defaultState(): ISpaceFollowMode {
    return {
      followingID: undefined,
      followingTabID: undefined,
      followingFrameID: undefined,
      followingInitLocation: undefined,
      followingSpaceView: undefined,
      followingSessionViewMetaData: undefined,
      followingExpandAndFullScreenChange: undefined,
    };
  }
}
